import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import {
  extensionPuzzleOutline,
  cardOutline,
  mailOutline,
  gridOutline,
  ticketOutline,
  fileTrayFullOutline,
  playOutline,
  settingsOutline,
  arrowBackOutline,
  trendingUpOutline,
  keyOutline,
  gitNetworkOutline,
  cubeOutline,
  serverOutline,
  addOutline
} from 'ionicons/icons';
import { menuController } from '@ionic/core/components';
import { useSidebarItems } from '../../hooks/useSidebarItems';
import { useProjects } from '../../hooks/useProjects';
import { isPinnedFor } from '../../hooks/usePinnedItems';
import { appPinId, agentPinId } from '../../utils/pinIds';
import { Apps } from '../../services/apps';
import LetterAvatar from '../atoms/LetterAvatar/LetterAvatar';
import '../../layouts/Sidebar.css'; // Import sidebar styles

// A pinned app/agent rendered as a sidebar nav-link. Shows the item's logo
// (falling back to a generated LetterAvatar on a missing/broken image, same
// as the list pages) + a truncated label, and navigates to its detail page.
const PinnedNavLink = ({ to, iconUrl, label, active, collapsed, onClick }) => {
  const [failed, setFailed] = useState(false);
  return (
    <li className="nav-item">
      <Link
        to={to}
        className={`nav-link nav-link--pinned ${active ? 'active' : ''}`}
        onClick={onClick}
        title={label}
      >
        <span className="nav-icon">
          {iconUrl && !failed ? (
            <img
              src={iconUrl}
              alt=""
              className="nav-pinned-icon"
              loading="lazy"
              onError={() => setFailed(true)}
            />
          ) : (
            <LetterAvatar name={label} className="nav-pinned-icon" />
          )}
        </span>
        {!collapsed && (
          <div className="nav-content">
            <span className="nav-label nav-label--pinned">{label}</span>
          </div>
        )}
      </Link>
    </li>
  );
};

// Custom "four circles" (2x2 dots) icon — ionicons only ships 9-dot (apps) or
// 4-square (grid), and the user wants four round dots. Rendered inline so it
// inherits currentColor + sizing like the ion-icons next to it.
const FourDotsIcon = (
  <svg viewBox="0 0 512 512" width="20" height="20" fill="none"
       stroke="currentColor" strokeWidth="34" aria-hidden="true">
    <circle cx="168" cy="168" r="54" />
    <circle cx="344" cy="168" r="54" />
    <circle cx="168" cy="344" r="54" />
    <circle cx="344" cy="344" r="54" />
  </svg>
);

const NavigationMenu = ({ className = '', isMobile = false, onNavClick, collapsed = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  // Current project — drives the PRIMARY nav links (Agents/Apps go to THIS
  // project's lists). The pinned list below is account-wide and ignores it.
  const { projectId } = useParams();

  // Pinned apps/agents in the sidebar — scoped to the CURRENT project (one
  // request, GET /workspace/sidebar?projectId). Shows THIS project's items and
  // changes when you switch projects. Pin state is per-project (default
  // pinned), checked via isPinnedFor.
  const { data: sidebar = { agents: [], apps: [] } } = useSidebarItems(projectId);
  const apps = sidebar.apps;
  const agents = sidebar.agents;

  // OWNER-ONLY Settings link. Project settings (integrations, repos, AI
  // credentials) belong to the project owner alone, so an invited member must
  // not even see the Settings nav item. `isOwner` is stamped server-side on
  // each project row (GET /projects). We read it from the account's cached
  // projects list (same query ProjectsPage already primes — no extra request in
  // the common case). Until the list resolves we treat the viewer as a
  // non-owner (hide Settings) rather than flash it to a member.
  const { data: projects = [] } = useProjects({ enabled: !!projectId });
  const currentProject = projectId
    ? projects.find((p) => p.projectId === projectId)
    : null;
  const isProjectOwner = currentProject?.isOwner === true;

  // App rows omit the catalog icon, so map appType → icon. Non-fatal — a miss
  // just falls back to a LetterAvatar. Only fetch the (heavy) apps catalog when
  // there is ACTUALLY a pinned app in the sidebar that needs an icon; otherwise
  // every page (incl. the agents marketplace) would fire a redundant apps-catalog
  // request purely for the sidebar. Re-runs if a pinned app appears later.
  const [appIconByType, setAppIconByType] = useState({});
  const pinnedAppCount = apps.length;
  useEffect(() => {
    if (pinnedAppCount === 0) return undefined;
    let alive = true;
    Apps.listCatalog()
      .then((data) => {
        if (!alive) return;
        const map = {};
        for (const c of (data?.items || [])) {
          if (c.appType) map[c.appType] = c.icon || null;
        }
        setAppIconByType(map);
      })
      .catch(() => { /* icons are best-effort; fall back to LetterAvatar */ });
    return () => { alive = false; };
  }, [pinnedAppCount]);

  // Build the pinned nav entries (agents first, then apps — mirrors the
  // Agents-above-Apps order of the primary nav). Each carries a stable id,
  // label, icon URL, and detail-page target.
  // Account-wide: each row carries its OWN projectId, so paths + the per-project
  // pin check both key off the entry's project (not the current one). No
  // projectId guard — the list shows everywhere and survives project switches.
  const pinnedEntries = [
    ...agents
      .filter((p) => isPinnedFor(p.projectId, agentPinId(p)))
      .map((p) => ({
        id: `agent:${p.projectId}:${agentPinId(p)}`,
        label: p.displayName || p.workflowType,
        iconUrl: p.iconUrl,
        path: `/projects/${p.projectId}/agents/${p.workflowType}`,
      })),
    ...apps
      .filter((a) => isPinnedFor(a.projectId, appPinId(a)))
      .map((a) => ({
        id: `app:${a.projectId}:${appPinId(a)}`,
        label: a.name || a.appType || a.appSlug || a.instanceId,
        iconUrl: appIconByType[a.appType] || appIconByType[a.appSlug] || null,
        path: a.deployMode === 'solo'
          ? `/projects/${a.projectId}/apps/solo/${a.appSlug}`
          : `/projects/${a.projectId}/apps/${a.instanceId}`,
      })),
  ];

  // Check if we're on a project-scoped page.
  //
  // The OLD top-level execution detail route /projects/:projectId/runs/:id
  // was rendered OUTSIDE ProjectLayout, so we excluded it to fall back to
  // the global nav. The check used `/runs/` as a substring — fine for the
  // old route but it ALSO caught the newer pipeline-scoped run page
  // (/projects/:id/pipelines/:type/runs/:exec) which IS inside a project
  // and should keep the project nav. Tighten the match to the exact
  // legacy shape: `/projects/<id>/runs/<id>` with no segments between.
  const isLegacyTopLevelRun = /^\/projects\/[^/]+\/runs\//.test(location.pathname);
  const isProjectPage = projectId && !isLegacyTopLevelRun;

  // Project-specific menu (when viewing a project).
  // Tickets + Issues are hidden from the sidebar — Heroku-style focus on
  // Pipelines as the primary unit. Routes still exist (deep links work);
  // we just don't surface them in the nav. Bring back by re-adding entries
  // with `ticketOutline` / `fileTrayFullOutline` if needed.
  // Imports for those icons are kept so reverting is one-line.
  // playOutline kept for the (removed) Test Runs entry — re-add in one line.
  void ticketOutline; void fileTrayFullOutline; void playOutline; void arrowBackOutline; void gridOutline;
  const projectMenuItems = [
    {
      section: 'Project',
      // Pinned apps/agents render immediately after this section's items
      // (i.e. after Apps). See the render loop below.
      showPinned: true,
      items: [
        {
          path: `/projects/${projectId}/agents`,
          label: 'Agents',
          icon: gitNetworkOutline,
          description: 'Deployed agents'
        },
        {
          path: `/projects/${projectId}/apps`,
          label: 'Apps',
          icon: cubeOutline,
          description: 'Deployed managed app instances'
        },
        {
          path: `/projects/${projectId}/stores`,
          label: 'Storage',
          icon: serverOutline,
          description: 'Databases and data stores'
        },
        {
          path: '/projects',
          label: 'Projects',
          customIcon: FourDotsIcon,
          description: 'All your projects'
        }
        // Test Runs intentionally removed from the sidebar — execution history
        // is reached from each agent/app's detail page. Route still exists.
      ]
    },
    {
      section: 'Navigation',
      // Anchored to the foot of the sidebar (above the user-profile block),
      // so Integrations + Settings sit at the bottom regardless of how many
      // pinned items are in the top group.
      bottomPinned: true,
      items: [
        // Settings is OWNER-ONLY — hidden for invited (non-owner) members.
        ...(isProjectOwner ? [{
          path: `/projects/${projectId}/settings`,
          label: 'Settings',
          icon: settingsOutline,
          description: 'Project configuration'
        }] : []),
        {
          path: '/integrations',
          label: 'Integrations',
          icon: extensionPuzzleOutline,
          description: 'Connect your services'
        }
      ]
    },
  ];

  // Global menu (when not on a project page)
  const globalMenuItems = [
    {
      section: 'Main',
      items: [
        {
          path: '/projects',
          label: t('nav.projects'),
          customIcon: FourDotsIcon,
          description: 'Your projects'
        },
        {
          path: '/integrations',
          label: t('nav.integrations'),
          icon: extensionPuzzleOutline,
          description: 'Connect your services'
        }
      ]
    },
    {
      section: 'Account',
      items: [
        {
          path: '/billing',
          label: t('nav.billing'),
          icon: cardOutline,
          description: 'Manage your subscription'
        },
        {
          path: '/usage',
          label: t('nav.usage'),
          icon: trendingUpOutline,
          description: 'View your usage'
        },
      ]
    },
    {
      section: 'Support',
      items: [
        {
          path: '/settings/tokens',
          label: 'Tokens',
          icon: keyOutline,
          description: 'Personal access tokens'
        },
        {
          path: '/contact',
          label: t('nav.contact'),
          icon: mailOutline,
          description: 'Get help and support'
        },
      ]
    }
  ];

  // Choose menu based on context
  const menuItems = isProjectPage ? projectMenuItems : globalMenuItems;

  const isActivePath = (path) => {
    if (path === '/integrations') {
      // `/` shows the Projects page, NOT integrations — used to falsely
      // light up this item on the root route (visible after the Sentry
      // OAuth callback bounced through /). Keep `/calendar*` as a
      // legacy alias since the calendar route redirects to /integrations.
      return location.pathname === '/integrations' || location.pathname.startsWith('/calendar');
    }
    if (path === '/projects') {
      // Root `/` IS the Projects page (see App.js routesPath.root →
      // ProjectsPage). Both URLs render the same view, so both should
      // highlight the Projects item in the sidebar.
      return location.pathname === '/' || location.pathname === '/projects';
    }
    // For project-specific routes
    if (projectId) {
      // Agents list + any agent detail page (`/agents/<type>`) + any nested
      // route under an agent (e.g. `/agents/<type>/runs/<id>` when the inline
      // run detail is open). Without the trailing-anything match, the
      // sidebar's Agents item lost its active state as soon as the user
      // clicked into a specific run. `agents` is the canonical segment; the
      // legacy `pipelines` aliases still resolve, so match both.
      if (path === `/projects/${projectId}/agents`) {
        return location.pathname === `/projects/${projectId}/agents` ||
               location.pathname === `/projects/${projectId}/pipelines` ||
               location.pathname.match(/^\/projects\/[^/]+\/(agents|pipelines)(\/.+)?$/) ||
               // Agents marketplace lives under /marketplace — there's no
               // standalone "Marketplace" nav item, so the agents surface
               // lights up Agents (its deploy target). Bare /marketplace
               // defaults to the agents tab; /marketplace/apps is the one
               // exception, handled by the Apps item below.
               (location.pathname.match(/^\/projects\/[^/]+\/marketplace(\/.*)?$/) &&
                !location.pathname.match(/^\/projects\/[^/]+\/marketplace\/apps(\/.*)?$/));
      }
      if (path === `/projects/${projectId}/tickets`) {
        // Match tickets list, ticket details, implementation details, and analysis details pages
        return location.pathname === `/projects/${projectId}/tickets` || 
               location.pathname.match(/^\/projects\/[^/]+\/tickets\/[^/]+$/) ||
               location.pathname.match(/^\/projects\/[^/]+\/implementations\/[^/]+$/) ||
               location.pathname.match(/^\/projects\/[^/]+\/analysis\/[^/]+$/);
      }
      if (path === `/projects/${projectId}/issues`) {
        return location.pathname === `/projects/${projectId}/issues` ||
               location.pathname.match(/^\/projects\/[^/]+\/issues\/[^/]+$/);
      }
      if (path === `/projects/${projectId}/settings`) {
        return location.pathname === `/projects/${projectId}/settings`;
      }
      if (path === `/projects/${projectId}/stores`) {
        // Storage & Database — flat list, no sub-routes today, but match a
        // trailing segment too so any future detail/deep-link keeps the item lit.
        return location.pathname === `/projects/${projectId}/stores` ||
               !!location.pathname.match(/^\/projects\/[^/]+\/stores(\/.+)?$/);
      }
      if (path === `/projects/${projectId}/marketplace`) {
        // Marketplace list + any future sub-routes (e.g. workflow detail
        // page outside the modal). Matches /marketplace and /marketplace/anything.
        return location.pathname === `/projects/${projectId}/marketplace` ||
               location.pathname.match(/^\/projects\/[^/]+\/marketplace(\/.+)?$/);
      }
      if (path === `/projects/${projectId}/apps`) {
        // Apps list + any app detail page (`/apps/<instanceId>`). Mirror of
        // the Pipelines matcher above so the sidebar item stays lit while
        // the user drills into one specific instance. Also lights up on the
        // apps marketplace surface (/marketplace/apps), the mirror of the
        // workflow-marketplace → Pipelines rule above.
        return location.pathname === `/projects/${projectId}/apps` ||
               location.pathname.match(/^\/projects\/[^/]+\/apps(\/.+)?$/) ||
               location.pathname.match(/^\/projects\/[^/]+\/marketplace\/apps(\/.*)?$/);
      }
      if (path === `/projects/${projectId}/testruns`) {
        // The fallback below catches "anything under /projects/<id>/ that
        // isn't an explicit other section" — when we added Pipelines as
        // its own nav item, /pipelines also fell through to here, lighting
        // both Pipelines AND Test Runs simultaneously. Add /pipelines to
        // the exclude list so only the right one highlights.
        return location.pathname === `/projects/${projectId}` ||
               location.pathname === `/projects/${projectId}/testruns` ||
               (location.pathname.startsWith(`/projects/${projectId}`) &&
                !location.pathname.includes('/tickets') &&
                !location.pathname.includes('/issues') &&
                !location.pathname.includes('/implementations') &&
                !location.pathname.includes('/analysis') &&
                !location.pathname.includes('/settings') &&
                !location.pathname.includes('/marketplace') &&
                !location.pathname.includes('/apps') &&
                !location.pathname.includes('/stores') &&
                !location.pathname.includes('/pipelines'));
      }
    }
    return location.pathname === path;
  };

  const handleNavClick = async () => {
    if (isMobile) {
      await menuController.close('sidebar-menu');
      if (onNavClick) onNavClick(); // Call parent callback to reset state
    }
  };

  // When the current page IS a pinned agent/app's detail, that pinned row owns
  // the active state — so suppress the top Agents/Apps active highlight (avoid
  // double-active). If the agent/app is NOT pinned, no pinned row matches and
  // the top item stays active as the fallback.
  const onPinnedDetail = pinnedEntries.some((e) => e.path === location.pathname);

  return (
    <nav className={`sidebar-nav ${className} ${collapsed ? 'sidebar-nav--collapsed' : ''}`}>
      {menuItems.map((section, index) => (
        <div key={index} className={`nav-section ${section.bottomPinned ? 'nav-section--bottom' : ''}`}>
          <ul className="nav-list">
            {section.items.map((item, itemIndex) => (
              <li key={itemIndex} className="nav-item">
                <Link
                  to={item.path}
                  className={`nav-link ${isActivePath(item.path) && !onPinnedDetail ? 'active' : ''} ${item.isBackButton ? 'back-button' : ''}`}
                  onClick={handleNavClick}
                  title={collapsed ? item.label : undefined}
                >
                  <span className="nav-icon">
                    {item.customIcon || <IonIcon icon={item.icon} />}
                  </span>
                  {!collapsed && (
                    <div className="nav-content">
                      <span className="nav-label">{item.label}</span>
                    </div>
                  )}
                </Link>
              </li>
            ))}
            {section.showPinned && (
              <li className="nav-pinned-divider" aria-hidden="true" />
            )}
            {section.showPinned && !collapsed && (
              <li className="nav-pinned-header">
                <span className="nav-pinned-header-label">Agents &amp; Apps</span>
                <Link
                  to={`/projects/${projectId}/marketplace`}
                  className="nav-pinned-add"
                  aria-label="Browse the marketplace"
                  onClick={handleNavClick}
                >
                  <IonIcon icon={addOutline} />
                </Link>
              </li>
            )}
            {section.showPinned && pinnedEntries.map((entry) => (
              <PinnedNavLink
                key={entry.id}
                to={entry.path}
                iconUrl={entry.iconUrl}
                label={entry.label}
                active={location.pathname === entry.path}
                collapsed={collapsed}
                onClick={handleNavClick}
              />
            ))}
            {/* Empty state: 3 STATIC placeholder rows (no loading animation) so
                the section reads as "nothing pinned yet" rather than blank. */}
            {section.showPinned && !collapsed && pinnedEntries.length === 0
              && [0, 1, 2].map((i) => (
                <li key={`pinned-skel-${i}`} className="nav-pinned-skeleton" aria-hidden="true">
                  <span className="nav-pinned-skeleton-icon" />
                  <span className="nav-pinned-skeleton-label" />
                </li>
              ))}
          </ul>
        </div>
      ))}
    </nav>
  );
};

export default NavigationMenu;
